/**
 * pt-BR is the source of truth for the key set: this file decides which keys
 * exist, and `en.ts` must supply an English string for every one of them
 * (enforced by `src/i18n/__tests__/i18n.test.ts`).
 *
 * This catalogue belongs to the shared foundation: the shell chrome, the auth
 * flow, and §6.6's four normative statements. The landing page (`index.html`)
 * is framework-free — it ships no script at all — so it hardcodes its own
 * copy directly rather than reading from here; there is nothing for a
 * `landing.*` key in this file to be read by. A feature area (the control
 * plane, the ops plane, tenant self-serve) ships its own strings through
 * `defineCatalogue` in `./index` and never edits this file — see the module
 * doc on `defineCatalogue` for why one shared file would be a collision.
 *
 * Each `estimator.statement.N` key is a translation of platform-design §6.6's
 * worked normative text, and is DELIBERATELY AHEAD OF ITS CONSUMER: the
 * burn-rate panel that renders them is work owned by the control plane and
 * tenant self-serve, and normative text ships as
 * a reviewed translation rather than being written under deadline by whoever
 * builds the panel. Its sibling `estimator.statement.N.source` key holds the
 * English verbatim.
 *
 * The English is pinned to the specification and not merely to itself:
 * `__tests__/fixtures/platform-design-6.6.md` is the §6.6 extract, held by
 * SHA-256 in `__tests__/i18n.test.ts`, and `en.ts`'s four statements are
 * compared against it. A reviewer checking a translation therefore has the
 * specification's own words in this repository, digest-pinned, rather than a
 * copy that moves whenever the thing it is supposed to check moves.
 */
export const messages = {
  // -- Shell chrome --------------------------------------------------------
  'shell.appName': 'SocialSrc',
  'shell.loading': 'Carregando…',
  // DELIBERATELY AHEAD OF ITS CONSUMER. A generic, pluralisable, interpolated
  // count for chrome such as a nav badge. It is the only key in this catalogue
  // carrying both a `{var}` and a plural form, so it is what exercises `t()`
  // and `plural()` honestly rather than through a test-only catalogue -- see
  // `__tests__/i18n.test.ts`, which is currently its only reader. The three
  // `shell.nav.*` labels that used to sit beside it were deleted once nav
  // labels became per-area thunks, which made them permanently unreachable.
  'shell.itemCount.one': '{count} item',
  'shell.itemCount.other': '{count} itens',
  // Both `createRootRoute` calls' `notFoundComponent` (`src/chrome/not-found.tsx`)
  // read this: the app plane and the ops plane share one unmatched-route
  // page, so it lives in the catalogue both may read.
  'notFound.title': 'Página não encontrada',
  'notFound.body': 'O endereço não corresponde a nenhuma página desta aplicação.',
  'notFound.home': 'Voltar ao início',

  // -- Auth flow ------------------------------------------------------------
  'auth.signIn': 'Entrar',
  'auth.signUp': 'Criar conta',
  'auth.name': 'Nome',
  'auth.email': 'E-mail',
  'auth.password': 'Senha',
  'auth.password.hint': 'Pelo menos 8 caracteres.',
  'auth.submit': 'Entrar',
  'auth.or': 'ou',
  'auth.error.invalidCredentials': 'E-mail ou senha inválidos.',
  'auth.error.generic': 'Não foi possível entrar. Tente novamente.',
  'auth.error.emailInUse': 'Este e-mail já está em uso.',
  'auth.error.weakPassword': 'A senha é muito fraca. Use pelo menos 6 caracteres.',
  'auth.error.network': 'Falha de conexão. Verifique sua internet e tente novamente.',
  'auth.error.tooManyRequests': 'Muitas tentativas. Aguarde um momento e tente novamente.',
  'auth.error.popupClosed': 'A janela do Google foi fechada antes de concluir. Tente novamente.',
  'auth.error.accountExists':
    'Este e-mail já tem uma conta com senha. Entre com o e-mail e a senha.',
  // Self-serve access, §17.2: sign-in offers a path to sign-up and Google is a
  // second method (architecture S1, §3), so each screen links to the other two.
  'auth.google': 'Continuar com Google',
  'auth.signUp.prompt': 'Primeira vez aqui?',
  'auth.signIn.prompt': 'Já tem conta?',
  'auth.forgotPassword': 'Esqueci minha senha',
  'auth.back': 'Voltar',
  'auth.resetPassword.heading': 'Recuperar senha',
  'auth.resetPassword.submit': 'Enviar o link',
  // Deliberately the same sentence whether or not the address has an account:
  // saying "not found" here is the reset-password form's version of the same
  // account-enumeration oracle a login form is (src/lib/auth.ts, toAuthFailure).
  'auth.resetPassword.success': 'Se esse e-mail tiver uma conta, enviamos um link de redefinição.',
  'auth.resetPassword.error': 'Não foi possível enviar o e-mail. Tente novamente.',
  'auth.resetPassword.sentTo': 'Link enviado para {email}',
  'auth.resetPassword.linkExpiry': 'O link expira em 1 hora.',
  'auth.resetPassword.tooManyAttempts': 'Muitas tentativas. Aguarde um momento e tente novamente.',
  'auth.resetPassword.resend': 'Enviar outro link',

  // -- Common -----------------------------------------------------------------
  'common.retry': 'Tentar novamente',

  // -- §6.6 burn-rate estimator: the four statements that accompany the
  //    estimate always, never as tooltips. Ahead of their consumer by design;
  //    see the module doc. Translations are technical
  //    Brazilian Portuguese, not a machine gloss; see the task report for how
  //    terms with no natural Portuguese equivalent (tenant, tier, p90) were
  //    handled.
  'estimator.statement.1':
    'O custo de comentários é determinado pelo volume de comentários, que você não controla. ' +
    'A estimativa usa a média recente da conta; uma publicação que viraliza custa proporcionalmente ' +
    'ao tamanho da sua thread. O painel precifica o cenário de cauda, p90 ou um valor informado pela ' +
    'organização, com e sem o limite por publicação, para que o limite seja definido com um número à vista.',
  'estimator.statement.1.source':
    'Comment cost is driven by comment volume, which you do not control. The estimate uses the ' +
    "account's recent average; a post that goes viral costs in proportion to its thread. The panel " +
    'prices the tail scenario, p90 or a figure the tenant enters, with and without the per-post cap, ' +
    'so the cap is set with a number in view.',

  'estimator.statement.2':
    'O custo de comentários depende da utilização das páginas. Buscar a cada rodada em uma ' +
    'publicação muito comentada compra latência, não mais comentários. O painel mostra a utilização ' +
    'por segmento e o piso por publicação.',
  'estimator.statement.2.source':
    'Comment cost depends on page utilisation. Pulling every round on a busy post buys latency, not ' +
    'more comments. The panel shows utilisation per segment and the floor per post.',

  'estimator.statement.3':
    'O primeiro segmento define um piso. A página mais nova é consultada nesse tier, haja ou não ' +
    'algo nela; essa é a sua latência de detecção de novas publicações.',
  'estimator.statement.3.source':
    'The first segment sets a floor. The newest page is polled at that tier whether or not anything ' +
    'is on it; this is your new-post detection latency.',

  'estimator.statement.4':
    'Toda coleta é feita ao vivo e cobrada integralmente, qualquer que seja o intervalo: o cache do ' +
    'provedor nunca responde a uma coleta agendada.',
  'estimator.statement.4.source':
    "Every fetch is live and fully billed, whatever the interval: the provider's cache is never " +
    'used to answer a scheduled pull.',
} as const;
